import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/benchmarkModel'
import { crudColorMap, crudIconMap, dokLevelSelectData, knowledgeTypeSelectData, kpiSelectData, zpdAnchorSelectData, zpdTagSelectData } from 'utils/helpers'

import { ActionType, IBenchmarkData } from 'types'
import { useGET_subjectsSelectQuery } from 'redux/api/subjectsApi'
import { useDELETE_measureMutation, useGET_goalClustersSelectQuery, useGET_goalComplexitiesSelectQuery, useGET_goalDomainsSelectQuery, useGET_measureByIdQuery, usePATCH_measureMutation, usePOST_measureMutation } from 'redux/api/goalbankApi'
import InputField from 'components/atoms/fields/InputField'
import CheckboxField from 'components/atoms/fields/CheckboxField'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const BenchmarkForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = '/benchmarks'
  const editSlug = `/benchmarks/edit/${id}`

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.benchmarks')}`,
    entityId: id,
    getEntityQuery: useGET_measureByIdQuery,
    createEntityMutation: usePOST_measureMutation,
    editEntityMutation: usePATCH_measureMutation,
    deleteEntityMutation: useDELETE_measureMutation,
  })

  const { code, title, diagnostic_indicator, subject, domain, cluster, complexity, 
    knowledge_type, zpd_anchor, dok_level } = formModel()

  const serverValues = entity ? {
    ...entity,
    knowledge_type: 
      entity.knowledge_type 
        ? {id: entity.knowledge_type, title: t(`knowledge-types.${entity.knowledge_type}`)} 
        : null,
    zpd_anchor: 
      entity.zpd_anchor 
        ? {id: entity.zpd_anchor, title: t(`zpd-anchors.${entity.zpd_anchor}`)} 
        : null,
    dok_level: entity.dok_level >= 0 ? {
      id: entity.dok_level,
      title:  entity.dok_level.toString()
    } : null,
  } : null

  const formValues = serverValues || initialValues

  return (
    <Formik
      enableReinitialize
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IBenchmarkData = {
          cluster_id: values.domain?.id ?? "",
          complexity_id: values.complexity?.id ?? "",
          code: values.code,
          title: values.title,
          diagnostic_indicator: values.diagnostic_indicator,
          knowledge_type: values.knowledge_type?.id ?? "",
          zpd_anchor: values.zpd_anchor?.id ?? "",
          dok_level: values.dok_level?.id.toString() ?? "",
        }
        handleSubmit(action, id, data)
      }}
    >{({ values, setFieldValue, errors }) => {
      return (
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>

          <Grid item xs={12} lg={2}>
             <InputField fieldProps={{ ...code }} multiline={true} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} lg={6}>
             <InputField fieldProps={{ ...title }} multiline={true} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} lg={4}>
             <InputField fieldProps={{ ...diagnostic_indicator }} multiline={true} disabled={viewOnly} />
          </Grid>


          <Grid item xs={12} lg={6} xl={4}>
              <SelectField
                    fieldProps={{ ...subject }}
                    apiCall={useGET_subjectsSelectQuery}
                    disabled={viewOnly}
                />
          </Grid>

          <Grid item xs={12} lg={6} xl={4}>
            <SelectField
                fieldProps={{ ...domain }}
                apiCall={useGET_goalDomainsSelectQuery}
                filters={{
                    subject: values.subject?.id ?? '',
                }}
                disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={6} xl={4}>
            <SelectField
                fieldProps={{ ...cluster }}
                apiCall={useGET_goalClustersSelectQuery}
                filters={{
                    subject: values.subject?.id ?? '',
                    domain: values.domain?.id ?? '',
                }}
                disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={6} xl={3}>
            <SelectField
                fieldProps={{ ...complexity }}
                apiCall={useGET_goalComplexitiesSelectQuery}
                disabled={viewOnly}
              />
          </Grid>

          <Grid item xs={12} lg={6} xl={3}>
            <SelectField
              fieldProps={{ ...knowledge_type }}
              apiCall={knowledgeTypeSelectData}
              disabled={viewOnly}
            />
          </Grid>
          
          <Grid item xs={12} lg={6} xl={3}>
            <SelectField
              fieldProps={{ ...zpd_anchor }}
              apiCall={zpdAnchorSelectData}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={6} xl={3}>
            <SelectField
              fieldProps={{ ...dok_level }}
              apiCall={dokLevelSelectData}
              disabled={viewOnly}
            />
          </Grid>


          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
      )
    }}
    </Formik>
  )
}
export default BenchmarkForm
